import { clsx } from 'clsx';
import { Zap, Snowflake, Box, AlertTriangle, Battery, Server } from 'lucide-react';
import { ContainerStats } from '../../types/configurator';

interface ContainerStatsBarProps {
  stats: ContainerStats;
}

export function ContainerStatsBar({ stats }: ContainerStatsBarProps) {
  const hasWarnings = stats.warnings.length > 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
        {/* Rack Space */}
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
            <Server className="h-3 w-3" />
            <span>Rack Space</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-gray-900">{stats.usedRackUnits}</span>
            <span className="text-sm text-gray-500">/ {stats.totalRackUnits}U</span>
          </div>
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-200">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                stats.totalRackUnits > 0 && stats.usedRackUnits / stats.totalRackUnits > 0.9
                  ? 'bg-red-500'
                  : stats.totalRackUnits > 0 && stats.usedRackUnits / stats.totalRackUnits > 0.7
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              )}
              style={{
                width: stats.totalRackUnits > 0
                  ? `${Math.min((stats.usedRackUnits / stats.totalRackUnits) * 100, 100)}%`
                  : '0%',
              }}
            />
          </div>
        </div>

        {/* Floor Space */}
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
            <Box className="h-3 w-3" />
            <span>Floor Space</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-gray-900">
              {Math.round(stats.floorSpaceUsedPercent)}%
            </span>
            <span className="text-sm text-gray-500">used</span>
          </div>
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-200">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                stats.floorSpaceUsedPercent > 80
                  ? 'bg-red-500'
                  : stats.floorSpaceUsedPercent > 60
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              )}
              style={{ width: `${Math.min(stats.floorSpaceUsedPercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Total Power */}
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
            <Zap className="h-3 w-3" />
            <span>Total Power</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-gray-900">
              {(stats.totalPowerWatts / 1000).toFixed(1)}
            </span>
            <span className="text-sm text-gray-500">kW</span>
          </div>
          {stats.pduCapacityWatts > 0 && (
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-200">
              <div
                className={clsx(
                  'h-full rounded-full transition-all',
                  stats.totalPowerWatts / stats.pduCapacityWatts > 0.9
                    ? 'bg-red-500'
                    : stats.totalPowerWatts / stats.pduCapacityWatts > 0.7
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                )}
                style={{
                  width: `${Math.min((stats.totalPowerWatts / stats.pduCapacityWatts) * 100, 100)}%`,
                }}
              />
            </div>
          )}
        </div>

        {/* PDU Capacity */}
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
            <Zap className="h-3 w-3" />
            <span>PDU Capacity</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-gray-900">
              {stats.pduCapacityWatts > 0 ? (stats.pduCapacityWatts / 1000).toFixed(1) : '—'}
            </span>
            <span className="text-sm text-gray-500">kW</span>
          </div>
          <p className="mt-1 text-[10px] text-gray-400">
            {stats.pduCapacityWatts > 0
              ? `${Math.round(stats.pduCapacityWatts - stats.totalPowerWatts)}W headroom`
              : 'No PDU configured'}
          </p>
        </div>

        {/* UPS */}
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
            <Battery className="h-3 w-3" />
            <span>UPS Backup</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-gray-900">
              {stats.upsCapacityVA > 0 ? (stats.upsCapacityVA / 1000).toFixed(1) : '—'}
            </span>
            <span className="text-sm text-gray-500">kVA</span>
          </div>
          <p className="mt-1 text-[10px] text-gray-400">
            {stats.upsRuntimeMinutes > 0
              ? `~${stats.upsRuntimeMinutes} min runtime`
              : 'No UPS configured'}
          </p>
        </div>

        {/* Cooling */}
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
            <Snowflake className="h-3 w-3" />
            <span>Cooling</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-gray-900">
              {stats.coolingCapacityBtu > 0 ? Math.round(stats.coolingCapacityBtu / 1000) : '—'}
            </span>
            <span className="text-sm text-gray-500">kBTU/hr</span>
          </div>
          <p className="mt-1 text-[10px] text-gray-400">
            {stats.coolingCapacityBtu > 0
              ? `Heat load: ${Math.round(stats.heatLoadBtu / 1000)}k BTU/hr`
              : 'No cooling configured'}
          </p>
        </div>
      </div>

      {/* Warnings */}
      {hasWarnings && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <div className="flex items-start gap-2 rounded bg-amber-50 p-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" />
            <div className="space-y-1">
              {stats.warnings.map((warning, i) => (
                <p key={i} className="text-xs text-amber-700">
                  {warning}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
